/**
 * 절차 생성 PBR 재질.
 *
 * 원칙: 한 표면의 모든 채널(albedo / normal / roughness / AO)은 **같은 구조 필드**에서
 * 파생된다. 채널마다 독립 노이즈를 쓰면 표면이 아니라 노이즈처럼 보인다.
 *
 *   안정 좌표 → 구조 필드(height) → 재질 정체성 → 미세구조 → PBR 채널
 *
 * 모든 난수는 시드 기반이라 누가 접속하든 같은 저택이 생성된다 (멀티플레이 전제).
 */

import * as THREE from 'three';

export type SurfaceId =
  | 'plaster' | 'oak' | 'tile' | 'concrete' | 'darkwood' | 'ceiling' | 'photowall'
  /* 마감 세트용 (2026-09-14, shared/finishes.ts). 벽 다섯 · 징두리 하나 · 바닥 둘 */
  | 'wall_school' | 'wall_paper' | 'wall_white' | 'wall_block' | 'wall_tile' | 'wains_wood' | 'vinyl' | 'tile_white'
  /* 그 밖의 카탈로그 표면(`SURFACE_CATALOG`) — 생성기로 찍어 낸 것. 문자열이면 다 받는다 */
  | (string & {});

/** 채널 디버그 뷰 */
export type ChannelView = 'off' | 'albedo' | 'normal' | 'roughness' | 'ao';

/**
 * 텍스처 해상도. 1인칭 근거리(1~4m)에서 tile 이 2m 안팎이므로 384 면 텍셀 밀도가 충분하고,
 * 생성 비용(=입장 지연)이 512 대비 절반 이하다. 저사양에서는 더 낮춘다.
 */
let RES = 384;

/** 화질 티어에 맞춰 텍스처 해상도를 정한다. surfaces() 호출 전에 불러야 한다. */
export function setSurfaceResolution(px: number) {
  RES = px;
}

// --- 결정론적 노이즈 ---

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 격자 값 노이즈. 격자를 wrap 하므로 텍스처가 이음매 없이 반복된다. */
function valueNoise(seed: number, n: number) {
  const rnd = mulberry32(seed);
  const g = new Float32Array(n * n);
  for (let i = 0; i < g.length; i++) g[i] = rnd();
  return (x: number, y: number) => {
    const fx = x * n;
    const fy = y * n;
    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const tx = fx - x0;
    const ty = fy - y0;
    const sx = tx * tx * (3 - 2 * tx);
    const sy = ty * ty * (3 - 2 * ty);
    const j0 = ((x0 % n) + n) % n;
    const j1 = ((x0 + 1) % n) % n;
    const i0 = ((y0 % n) + n) % n;
    const i1 = ((y0 + 1) % n) % n;
    const a = g[i0 * n + j0];
    const b = g[i0 * n + j1];
    const c = g[i1 * n + j0];
    const d = g[i1 * n + j1];
    const top = a + (b - a) * sx;
    const bot = c + (d - c) * sx;
    return top + (bot - top) * sy;
  };
}

/** 옥타브 합. octaves[i] = [격자수, 가중치] */
function fbm(seed: number, octaves: [number, number][]) {
  const layers = octaves.map(([n, w], i) => [valueNoise(seed + i * 7919, n), w] as const);
  const total = octaves.reduce((s, [, w]) => s + w, 0);
  return (x: number, y: number) => {
    let v = 0;
    for (const [f, w] of layers) v += f(x, y) * w;
    return v / total;
  };
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (e0: number, e1: number, x: number) => {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
};
const mix = (a: number, b: number, t: number) => a + (b - a) * t;

// --- 표면 정의 ---

interface Surface {
  /** 텍스처 한 장이 덮는 실제 크기(m) — 텍셀 밀도의 근거 */
  tile: number;
  /** 노멀 세기 */
  normalStrength: number;
  /**
   * 픽셀 하나를 푼다.
   * @returns [r, g, b, height(0..1), roughness(0..1)]
   */
  shade(x: number, y: number): [number, number, number, number, number];
}

function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

function tintedWood(base: [number, number, number], grain: number, tone: number) {
  // 나뭇결은 색과 광택을 함께 바꾼다 — 결이 진한 곳은 어둡고 덜 반사한다
  const k = 0.68 + tone * 0.22 - grain * 0.26;
  return [base[0] * k, base[1] * k, base[2] * k] as [number, number, number];
}

/* ────────────────────────────────────────────────────────────────────
 * **표면 생성기** (2026-09-14, Chris: 「종류가 더 다양하면 — 범용성 있게, 다른 게임에서도 쓸 거」)
 * 손으로 한 case 씩 짜면 열 개가 한계다. 페인트·벽지·타일·널·블록·돌·카펫 **일곱 생성기**에 색과
 * 치수만 넣어 카탈로그를 찍어 낸다. 이 표면들은 사흘 전용이 아니다 — 게임이 바뀌면 카탈로그만 고른다.
 * 촌스러워지는 건 언제나 채도다: 기본 색은 전부 회색 쪽으로 눌러 두고, 튀는 색은 넣지 않는다.
 * ──────────────────────────────────────────────────────────────────── */
type Gen = () => Surface;
const GEN = {
  /** 수성 페인트 — 롤러 스티플 + 넓은 얼룩. `streak` 를 주면 위에서 흘러내린 물얼룩 */
  paint: (hex: string, o: { rough?: number; streak?: number; seed?: number } = {}): Gen => () => {
    const sd = o.seed ?? 3001;
    const stip = fbm(sd, [[70, 1], [180, 0.6]]);
    const patch = fbm(sd + 30, [[3, 1], [8, 0.5]]);
    const base = hexToRgb(hex);
    const st = o.streak ?? 0;
    return { tile: 1.4, normalStrength: 0.2, shade(x, y) {
      const sp = stip(x, y), pt = patch(x, y);
      const stain = st ? smooth(0.55, 0.9, patch(x * 0.6, y * 0.25)) * (1 - y * 0.6) * st : 0;
      const k = 0.95 + sp * 0.05 - pt * 0.04 - stain * 0.09;
      return [base[0] * k, base[1] * k, base[2] * (k - 0.012), clamp01(0.5 + sp * 0.14), clamp01((o.rough ?? 0.84) + pt * 0.06 + stain * 0.06)];
    } };
  },
  /** 벽지 — 53cm 롤 이음선 + 무늬. dot 마름모점 · stripe 세로줄 · floral 잔꽃(점 셋) · plain 무지 */
  paper: (hex: string, pattern: 'dot' | 'stripe' | 'floral' | 'plain', o: { yellow?: number; seed?: number; ink?: string } = {}): Gen => () => {
    const sd = o.seed ?? 3101;
    const yellow = fbm(sd, [[2, 1], [6, 0.4]]);
    const fiber = fbm(sd + 30, [[90, 1], [220, 0.5]]);
    const base = hexToRgb(hex), ink = hexToRgb(o.ink ?? '#000000');
    const yl0 = o.yellow ?? 0.5;
    return { tile: 1.06, normalStrength: 0.18, shade(x, y) {
      const u = x * 2, v = y * 2 * (1.06 / 0.36);
      const fu = u - Math.floor(u);
      const seam = 1 - smooth(0.0, 0.012, Math.min(fu, 1 - fu));
      let pat = 0;
      if (pattern === 'dot') pat = Math.max(0, 1 - Math.hypot((u * 6) % 1 - 0.5, (v * 6) % 1 - 0.5) * 3.2) * 0.05;
      else if (pattern === 'stripe') pat = (Math.sin(u * 2 * Math.PI * 10) > 0.6 ? 1 : 0) * 0.035;
      else if (pattern === 'floral') { const gu = (u * 4) % 1, gv = (v * 4) % 1; pat = (Math.max(0, 1 - Math.hypot(gu - 0.5, gv - 0.5) * 6) + Math.max(0, 1 - Math.hypot(gu - 0.32, gv - 0.4) * 9) + Math.max(0, 1 - Math.hypot(gu - 0.66, gv - 0.62) * 9)) * 0.06; }
      const yl = yellow(x * 0.8, y * 0.5) * yl0;
      const k = 0.9 + fiber(x, y) * 0.06 + yl * 0.08 - seam * 0.05;
      const r = base[0] * k * (1 - pat) + ink[0] * pat, g = base[1] * (k - yl * 0.03) * (1 - pat) + ink[1] * pat, b2 = base[2] * (k - yl * 0.09) * (1 - pat) + ink[2] * pat;
      return [r, g, b2, clamp01(0.5 + pat * 0.6 - seam * 0.2), clamp01(0.82 + fiber(x, y) * 0.06)];
    } };
  },
  /** 타일 — 한 변 `size`m, 줄눈 폭, 광택. 벽·바닥 공용 */
  tiles: (hex: string, size: number, o: { grout?: number; gloss?: number; wear?: number; seed?: number; groutHex?: string } = {}): Gen => () => {
    const sd = o.seed ?? 3201;
    const speck = fbm(sd, [[50, 1], [120, 0.5]]);
    const wearF = fbm(sd + 30, [[4, 1], [10, 0.4]]);
    const rnd = mulberry32(sd + 40);
    const N = 4; const tone: number[] = []; for (let i = 0; i < N * N; i++) tone.push(rnd());
    const base = hexToRgb(hex), gr = hexToRgb(o.groutHex ?? '#7a766f');
    const gw = (o.grout ?? 0.006) / size;
    return { tile: size * N, normalStrength: 1.0, shade(x, y) {
      const tx = x * N, ty = y * N;
      const fx = tx - Math.floor(tx), fy = ty - Math.floor(ty);
      const edge = Math.min(Math.min(fx, 1 - fx), Math.min(fy, 1 - fy));
      const grout = 1 - smooth(0.0, gw * 2.2, edge);
      const t = tone[(Math.floor(tx) % N) + (Math.floor(ty) % N) * N];
      const w = wearF(x, y) * (o.wear ?? 0.5);
      const k = mix(0.93, 1.04, t) - w * 0.08 + speck(x, y) * 0.03;
      return [mix(base[0] * k, gr[0], grout), mix(base[1] * k, gr[1], grout), mix(base[2] * (k - 0.01), gr[2], grout), clamp01(0.8 - grout * 0.8), clamp01(mix(1 - (o.gloss ?? 0.75) + w * 0.2, 0.92, grout))];
    } };
  },
  /** 널 — 폭 `w`m 의 판을 나란히. vertical 이면 세로(징두리), 아니면 가로(마루) */
  boards: (hex: string, w: number, o: { vertical?: boolean; groove?: number; rough?: number; seed?: number; len?: number } = {}): Gen => () => {
    const sd = o.seed ?? 3301;
    const grain = fbm(sd, [[6, 1], [40, 0.5], [120, 0.3]]);
    const rnd = mulberry32(sd + 40);
    const N = Math.max(2, Math.round((o.len ?? 0.9) / w));
    const tone: number[] = []; for (let i = 0; i < 64; i++) tone.push(rnd());
    const base = hexToRgb(hex);
    const tileM = N * w;
    return { tile: tileM, normalStrength: 0.8, shade(x, y) {
      const a = o.vertical ? x : y, b2 = o.vertical ? y : x;
      const u = a * N, fu = u - Math.floor(u);
      const groove = 1 - smooth(0.0, (o.groove ?? 0.006) / w, Math.min(fu, 1 - fu));
      const i = Math.floor(u) % N;
      // 가로 널은 길이 방향으로도 끊긴다(엇갈린 이음) — 없으면 줄무늬 카펫이다
      const seamAlong = o.vertical ? 0 : (1 - smooth(0.0, 0.004, Math.abs(((b2 * 1.0 + tone[i] * 0.7) % 1) - 0.5) < 0.002 ? 0 : 1)) ;
      const t = tone[(i + Math.floor(b2 + tone[i])) % 64];
      const g = grain(o.vertical ? x * 0.15 + t : x, o.vertical ? y : y * 0.15 + t);
      const k = 0.88 + t * 0.14 + g * 0.1 - groove * 0.35 - seamAlong * 0.3;
      return [base[0] * k, base[1] * (k - 0.01), base[2] * (k - 0.03), clamp01(0.6 + g * 0.08 - groove * 0.6), clamp01((o.rough ?? 0.62) + g * 0.08 + groove * 0.2)];
    } };
  },
  /** 시멘트 블록 / 벽돌 — 가로 `bw` × 세로 `bh` m, 엇갈린 줄 */
  blocks: (hex: string, bw: number, bh: number, o: { grout?: number; seed?: number; groutHex?: string; tone?: number } = {}): Gen => () => {
    const sd = o.seed ?? 3401;
    const grain = fbm(sd, [[50, 1], [130, 0.5]]);
    const rnd = mulberry32(sd + 40);
    const tone: number[] = []; for (let i = 0; i < 16; i++) tone.push(rnd());
    const base = hexToRgb(hex), gr = hexToRgb(o.groutHex ?? '#6e6a63');
    const cols = 2, rows = Math.max(2, Math.round((bw * cols) / bh));
    const tileM = bw * cols;
    return { tile: tileM, normalStrength: 1.0, shade(x, y) {
      const row = Math.floor(y * rows);
      const u = x * cols + (row % 2) * 0.5, v = y * rows;
      const fu = u - Math.floor(u), fv = v - Math.floor(v);
      const edge = Math.min(Math.min(fu, 1 - fu) * bw, Math.min(fv, 1 - fv) * bh);
      const grout = 1 - smooth(0.0, o.grout ?? 0.01, edge);
      const t = tone[((Math.floor(u) & 3) + (row & 3) * 4) & 15];
      const g = grain(x, y);
      const k = 0.9 + t * (o.tone ?? 0.12) + g * 0.06;
      return [mix(base[0] * k, gr[0], grout), mix(base[1] * k, gr[1], grout), mix(base[2] * (k - 0.01), gr[2], grout), clamp01(0.75 + g * 0.1 - grout * 0.7), clamp01(0.9 - grout * 0.05)];
    } };
  },
  /** 돌·테라조·시멘트 — 불규칙 얼룩. 바닥용 */
  stone: (hex: string, o: { speck?: string; rough?: number; seed?: number; scale?: number } = {}): Gen => () => {
    const sd = o.seed ?? 3501;
    const broad = fbm(sd, [[3, 1], [7, 0.5]]);
    const fine = fbm(sd + 30, [[30, 1], [90, 0.5]]);
    const base = hexToRgb(hex), sp = o.speck ? hexToRgb(o.speck) : null;
    return { tile: o.scale ?? 2.0, normalStrength: 0.45, shade(x, y) {
      const b2 = broad(x, y), f = fine(x, y);
      const fleck = sp ? smooth(0.78, 0.86, fine(x * 1.7, y * 1.7)) : 0;
      const k = 0.88 + b2 * 0.14 + f * 0.06;
      return [mix(base[0] * k, sp ? sp[0] : 0, fleck), mix(base[1] * k, sp ? sp[1] : 0, fleck), mix(base[2] * k, sp ? sp[2] : 0, fleck), clamp01(0.5 + f * 0.1), clamp01((o.rough ?? 0.7) + b2 * 0.1)];
    } };
  },
  /** 카펫 — 짧은 파일. 바닥용 */
  carpet: (hex: string, o: { seed?: number } = {}): Gen => () => {
    const sd = o.seed ?? 3601;
    const pile = fbm(sd, [[120, 1], [260, 0.6]]);
    const wear = fbm(sd + 30, [[2, 1], [5, 0.5]]);
    const base = hexToRgb(hex);
    return { tile: 1.0, normalStrength: 0.3, shade(x, y) {
      const pl = pile(x, y), w = wear(x, y);
      const k = 0.9 + pl * 0.1 - w * 0.06;
      return [base[0] * k, base[1] * k, base[2] * k, clamp01(0.5 + pl * 0.2), 0.97];
    } };
  },
};

/* ────────────────────────────────────────────────────────────────────
 * **패턴 × 색상** (2026-09-14, Chris: 「다양성이 없어 보이는데, 심즈 스타일」)
 * 심즈는 패턴(무늬·재질)을 고르면 그 패턴의 **색 견본**이 펼쳐진다 — 페인트 한 패턴에 색 열둘.
 * 패턴마다 색이 하나뿐이면 마흔 개를 늘어놔도 빈약하다. 표면 id 는 `패턴:색키` 다.
 * 분류는 심즈를 따른다 — 벽: 페인트·벽지·타일·판넬·조적·석재·사이딩 / 바닥: 목재·카펫·타일·석재·조적·리놀륨·금속·야외
 * ──────────────────────────────────────────────────────────────────── */
export interface Swatch { key: string; name: string; hex: string }
export interface SurfacePattern {
  id: string; name: string; kind: 'wall' | 'wains' | 'floor'; group: string;
  /** 색을 받아 생성기를 낸다 */
  gen: (hex: string) => Gen;
  colors: Swatch[];
}
const C = (key: string, name: string, hex: string): Swatch => ({ key, name, hex });
/** 눌러 잡은 페인트 팔레트 — 순색을 넣으면 유치원이 된다. 열두 색 전부 회색이 섞여 있다 */
const PAINTS: Swatch[] = [
  C('white', '흰색', '#dcd9d0'), C('cream', '미색', '#d8d0bd'), C('ivory', '아이보리', '#e3dcc6'), C('grey', '회색', '#a9aaa6'),
  C('sage', '회녹', '#8d938a'), C('mint', '병원 민트', '#a7bdb2'), C('sky', '옅은 하늘', '#b9c6cc'), C('blue', '먹빛 파랑', '#6f7f8f'),
  C('sand', '모래', '#c9b995'), C('peach', '살구', '#d1b3a0'), C('olive', '올리브', '#8b8c6a'), C('dark', '짙은 회색', '#5c5f62'),
];
const PAPERS: Swatch[] = [
  C('yellow', '누런', '#c4b38f'), C('white', '흰', '#dcd8cf'), C('beige', '베이지', '#cfc6b3'), C('green', '녹색', '#a9b39e'),
  C('rose', '연분홍', '#cdb5b0'), C('blue', '연파랑', '#b6c0c8'), C('old', '삭은', '#b7a98a'), C('grey', '회색', '#b8b6b0'),
];
const WOODS: Swatch[] = [
  C('oak', '참나무', '#a8895f'), C('pine', '소나무', '#b89768'), C('walnut', '호두', '#66533f'), C('dark', '짙은', '#4d3f31'),
  C('ash', '물푸레', '#c4b598'), C('cherry', '벚나무', '#8f5f47'), C('white', '흰 칠', '#d9d6cd'), C('grey', '회색 칠', '#8e8d88'),
];
const TILES: Swatch[] = [
  C('white', '흰', '#e2e0d9'), C('beige', '베이지', '#b3ada2'), C('blue', '청색', '#8fa7b3'), C('green', '청록', '#8fa89c'),
  C('black', '검정', '#3a3b3a'), C('terra', '테라코타', '#a86d4e'), C('grey', '회색', '#9a9a96'), C('pink', '분홍', '#c9a9a4'),
];
const STONES: Swatch[] = [
  C('grey', '회색', '#8c8b86'), C('warm', '따뜻한 회색', '#a09a90'), C('dark', '짙은', '#5f5e5a'), C('sand', '사암', '#bfae90'), C('white', '흰 대리석', '#d8d5cf'),
];
const BRICKS: Swatch[] = [
  C('red', '붉은', '#8a5a4a'), C('brown', '갈색', '#6f5648'), C('white', '흰 칠', '#d2cfc6'), C('grey', '회색', '#8b8681'), C('yellow', '노란', '#b9a276'), C('dark', '검붉은', '#5a3f3a'),
];
const CARPETS: Swatch[] = [
  C('grey', '회색', '#7d7f7c'), C('red', '팥색', '#6f3f3d'), C('blue', '남색', '#4b5566'), C('beige', '베이지', '#a99f8a'), C('green', '녹색', '#5f6b58'), C('brown', '갈색', '#6e5b4a'),
];
const VINYLS: Swatch[] = [
  C('yellow', '누런', '#c7a765'), C('grey', '회색', '#9d9c97'), C('wood', '나무결', '#b4915e'), C('green', '녹색', '#8c9a88'), C('brown', '갈색', '#8a6f52'),
];

export const SURFACE_PATTERNS: SurfacePattern[] = [
  // ── 벽
  { id: 'paint',        name: '수성 페인트',   kind: 'wall', group: '페인트', colors: PAINTS, gen: (h) => GEN.paint(h, { seed: 3001 }) },
  { id: 'paint_stain',  name: '물얼룩 페인트', kind: 'wall', group: '페인트', colors: PAINTS, gen: (h) => GEN.paint(h, { seed: 3016, streak: 1 }) },
  { id: 'paint_rough',  name: '거친 페인트',   kind: 'wall', group: '페인트', colors: PAINTS, gen: (h) => GEN.paint(h, { seed: 3021, rough: 0.96 }) },
  { id: 'paper_dot',    name: '점무늬 벽지',   kind: 'wall', group: '벽지', colors: PAPERS, gen: (h) => GEN.paper(h, 'dot', { seed: 3101 }) },
  { id: 'paper_plain',  name: '무지 벽지',     kind: 'wall', group: '벽지', colors: PAPERS, gen: (h) => GEN.paper(h, 'plain', { seed: 3111, yellow: 0.2 }) },
  { id: 'paper_stripe', name: '줄무늬 벽지',   kind: 'wall', group: '벽지', colors: PAPERS, gen: (h) => GEN.paper(h, 'stripe', { seed: 3112, ink: '#7a7062' }) },
  { id: 'paper_floral', name: '잔꽃 벽지',     kind: 'wall', group: '벽지', colors: PAPERS, gen: (h) => GEN.paper(h, 'floral', { seed: 3113, ink: '#6f5f52' }) },
  { id: 'tile_15',      name: '타일 15cm',     kind: 'wall', group: '타일', colors: TILES, gen: (h) => GEN.tiles(h, 0.15, { seed: 3201, gloss: 0.8, groutHex: '#9a978f' }) },
  { id: 'tile_10',      name: '타일 10cm',     kind: 'wall', group: '타일', colors: TILES, gen: (h) => GEN.tiles(h, 0.10, { seed: 3211, gloss: 0.8, groutHex: '#c8c8c2' }) },
  { id: 'tile_5',       name: '모자이크 5cm',  kind: 'wall', group: '타일', colors: TILES, gen: (h) => GEN.tiles(h, 0.05, { seed: 3231, gloss: 0.7, groutHex: '#b5b3ad', grout: 0.003 }) },
  { id: 'panel_board',  name: '널 판넬',       kind: 'wall', group: '판넬', colors: WOODS, gen: (h) => GEN.boards(h, 0.12, { vertical: true, seed: 3311, rough: 0.55 }) },
  { id: 'panel_ply',    name: '합판',          kind: 'wall', group: '판넬', colors: WOODS, gen: (h) => GEN.boards(h, 1.2, { vertical: true, seed: 3312, groove: 0.004, rough: 0.7, len: 2.4 }) },
  { id: 'block',        name: '시멘트 블록',   kind: 'wall', group: '조적', colors: STONES, gen: (h) => GEN.blocks(h, 0.39, 0.19, { seed: 3401 }) },
  { id: 'brick',        name: '벽돌',          kind: 'wall', group: '조적', colors: BRICKS, gen: (h) => GEN.blocks(h, 0.19, 0.057, { seed: 3411, groutHex: '#8f8a82', tone: 0.2 }) },
  { id: 'plaster_rough',name: '거친 회벽',     kind: 'wall', group: '석재', colors: STONES, gen: (h) => GEN.stone(h, { seed: 3531, rough: 0.92, scale: 2.4 }) },
  { id: 'stone_block',  name: '석재 쌓기',     kind: 'wall', group: '석재', colors: STONES, gen: (h) => GEN.blocks(h, 0.6, 0.3, { seed: 3421, grout: 0.008, tone: 0.1 }) },
  { id: 'siding',       name: '가로 사이딩',   kind: 'wall', group: '사이딩', colors: PAINTS, gen: (h) => GEN.boards(h, 0.2, { seed: 3331, rough: 0.75, len: 3.0 }) },
  { id: 'tin',          name: '함석 골판',     kind: 'wall', group: '사이딩', colors: [C('zinc', '아연', '#9ea3a6'), C('rust', '녹슨', '#8a6a55'), C('blue', '청색', '#5f7383'), C('green', '녹색', '#6f7f6a')], gen: (h) => GEN.boards(h, 0.076, { vertical: true, seed: 3341, groove: 0.02, rough: 0.5, len: 0.912 }) },
  // ── 징두리
  { id: 'wains_board',  name: '널 징두리',     kind: 'wains', group: '징두리', colors: WOODS, gen: (h) => GEN.boards(h, 0.09, { vertical: true, seed: 3313, rough: 0.6 }) },
  { id: 'wains_tile',   name: '타일 허리',     kind: 'wains', group: '징두리', colors: TILES, gen: (h) => GEN.tiles(h, 0.15, { seed: 3201, gloss: 0.8, groutHex: '#9a978f' }) },
  { id: 'wains_paint',  name: '페인트 허리',   kind: 'wains', group: '징두리', colors: PAINTS, gen: (h) => GEN.paint(h, { seed: 3031, rough: 0.7 }) },
  // ── 바닥
  { id: 'plank',        name: '마루 널',       kind: 'floor', group: '목재', colors: WOODS, gen: (h) => GEN.boards(h, 0.14, { seed: 3321, rough: 0.6, len: 2.8 }) },
  { id: 'plank_wide',   name: '넓은 널',       kind: 'floor', group: '목재', colors: WOODS, gen: (h) => GEN.boards(h, 0.22, { seed: 3323, rough: 0.55, len: 3.3 }) },
  { id: 'parquet',      name: '쪽마루',        kind: 'floor', group: '목재', colors: WOODS, gen: (h) => GEN.boards(h, 0.07, { seed: 3322, rough: 0.5, len: 0.42 }) },
  { id: 'carpet',       name: '카펫',          kind: 'floor', group: '카펫', colors: CARPETS, gen: (h) => GEN.carpet(h, { seed: 3611 }) },
  { id: 'ftile_30',     name: '타일 30cm',     kind: 'floor', group: '타일', colors: TILES, gen: (h) => GEN.tiles(h, 0.3, { seed: 3221, gloss: 0.7, groutHex: '#8b8378' }) },
  { id: 'ftile_20',     name: '타일 20cm',     kind: 'floor', group: '타일', colors: TILES, gen: (h) => GEN.tiles(h, 0.2, { seed: 3223, gloss: 0.65, groutHex: '#8b8378' }) },
  { id: 'ftile_check',  name: '체크 타일',     kind: 'floor', group: '타일', colors: [C('bw', '흑백', '#d9d6cf'), C('grey', '회색', '#b9b6af'), C('cream', '크림', '#e0d7c2')], gen: (h) => GEN.tiles(h, 0.3, { seed: 3225, gloss: 0.7, groutHex: '#2f2f2e', grout: 0.15 }) },
  { id: 'fstone',       name: '석재',          kind: 'floor', group: '석재', colors: STONES, gen: (h) => GEN.blocks(h, 0.6, 0.4, { seed: 3421, groutHex: '#6a6863', grout: 0.008, tone: 0.1 }) },
  { id: 'terrazzo',     name: '테라조',        kind: 'floor', group: '석재', colors: STONES, gen: (h) => GEN.stone(h, { seed: 3522, rough: 0.4, speck: '#5a5650', scale: 1.2 }) },
  { id: 'fbrick',       name: '벽돌 바닥',     kind: 'floor', group: '조적', colors: BRICKS, gen: (h) => GEN.blocks(h, 0.19, 0.09, { seed: 3431, groutHex: '#8f8a82', tone: 0.18 }) },
  { id: 'cement',       name: '시멘트 미장',   kind: 'floor', group: '조적', colors: STONES, gen: (h) => GEN.stone(h, { seed: 3521, rough: 0.8 }) },
  { id: 'lino',         name: '장판',          kind: 'floor', group: '리놀륨', colors: VINYLS, gen: (h) => GEN.stone(h, { seed: 3511, rough: 0.42, scale: 1.8 }) },
  { id: 'metal_plate',  name: '체크 철판',     kind: 'floor', group: '금속', colors: [C('steel', '강철', '#8d9094'), C('dark', '검은 철', '#4e5154'), C('rust', '녹슨', '#7a5d4c')], gen: (h) => GEN.tiles(h, 0.6, { seed: 3701, gloss: 0.5, groutHex: '#5a5c5f', grout: 0.004 }) },
  { id: 'gravel',       name: '자갈',          kind: 'floor', group: '야외', colors: [C('grey', '회색', '#8f8d86'), C('brown', '갈색', '#8a7c68')], gen: (h) => GEN.stone(h, { seed: 3801, rough: 0.95, speck: '#c9c4bb', scale: 0.8 }) },
  { id: 'deck',         name: '데크',          kind: 'floor', group: '야외', colors: WOODS, gen: (h) => GEN.boards(h, 0.12, { seed: 3811, rough: 0.8, len: 2.4, groove: 0.012 }) },
];
export const PATTERN_BY_ID: Record<string, SurfacePattern> = Object.fromEntries(SURFACE_PATTERNS.map((p) => [p.id, p]));
/** `패턴:색키` → 사람이 읽는 이름 */
export function surfaceName(id: string): string {
  const [pid, ck] = id.split(':');
  const pat = PATTERN_BY_ID[pid];
  if (!pat) return LEGACY_NAMES[id] ?? id;
  const c = pat.colors.find((q) => q.key === ck);
  return c ? `${c.name} ${pat.name}` : pat.name;
}
const LEGACY_NAMES: Record<string, string> = { oak: '마루', darkwood: '짙은 마루', tile: '베이지 타일', concrete: '콘크리트', plaster: '회벽', vinyl: '누런 장판', tile_white: '흰 타일', wall_school: '회녹색 페인트', wall_paper: '누런 벽지', wall_white: '흰 페인트', wall_block: '시멘트 블록', wall_tile: '흰 타일', wains_wood: '나무 징두리' };

/** `패턴:색키` 를 생성기로. 옛 고정 id 는 `LEGACY_DEFS` 에서 */
function resolveDef(id: string): Gen | null {
  const i = id.indexOf(':');
  if (i > 0) {
    const pat = PATTERN_BY_ID[id.slice(0, i)];
    const c = pat?.colors.find((q) => q.key === id.slice(i + 1));
    if (pat && c) return pat.gen(c.hex);
  }
  return LEGACY_DEFS[id] ?? null;
}
const LEGACY_DEFS: Record<string, Gen> = {
  wall_cream:   GEN.paint('#d8d0bd', { seed: 3011 }),
  wall_grey:    GEN.paint('#a9aaa6', { seed: 3012 }),
  paper_plain:  GEN.paper('#dcd8cf', 'plain', { seed: 3111, yellow: 0.2 }),
  brick_red:    GEN.blocks('#8a5a4a', 0.19, 0.057, { seed: 3411, groutHex: '#8f8a82', tone: 0.2 }),
  wains_white:  GEN.boards('#d9d6cd', 0.09, { vertical: true, seed: 3314, rough: 0.6 }),
  tile_check:   GEN.tiles('#d9d6cf', 0.3, { seed: 3221, gloss: 0.7, groutHex: '#2f2f2e', grout: 0.15 }),
};

function makeSurface(id: SurfaceId): Surface {
  const gen = resolveDef(id as string);
  if (gen) return gen();
  switch (id) {
    default:
      console.warn(`[materials] 모르는 표면 '${id}' — 회색 페인트로 대신한다`);
      return GEN.paint('#a9aaa6')();
    // 송이경의 벽. 「이 년 치 사진이 벽 세 면을 덮는다」고 써 놓고 민무늬 합판을
    // 세워 뒀더니 공사장 판때기로 보였다. 사진 한 장 한 장을 결로 만든다.
    // 인화지가 벽보다 조금 튀어나오게 높이를 줘서, 빛이 스치면 장마다 그림자가 진다.
    case 'photowall': {
      const jitter = fbm(733, [[9, 1]]);
      const grain = fbm(911, [
        [40, 1],
        [110, 0.5],
      ]);
      return {
        tile: 0.62, // 한 칸에 넉 장 x 세 줄 -> 사진 한 장이 대략 13cm
        normalStrength: 0.34,
        shade(x, y) {
          const u = x * 4;
          const v = y * 3;
          const cu = Math.floor(u);
          const cv = Math.floor(v);
          const fu = u - cu;
          const fv = v - cv;
          const seed = jitter(cu * 0.77 + 0.13, cv * 0.53 + 0.29);

          // 사진 사이로 보이는 벽. 빈틈 없이 붙이면 벽지처럼 보인다
          const pad = 0.07 + seed * 0.05;
          if (fu < pad || fu > 1 - pad || fv < pad || fv > 1 - pad) {
            return [66, 62, 57, 0.0, 0.95];
          }
          // 인화지 흰 여백
          const edge = pad + 0.07;
          if (fu < edge || fu > 1 - edge || fv < edge || fv > 1 - edge) {
            const w = 214 - seed * 26;
            return [w, w * 0.985, w * 0.94, 0.8, 0.52];
          }
          // 사진 알맹이. 흑백과 세피아가 섞여 있다 — 이 사람은 필름을 쓴다
          const t = 46 + jitter(u * 2.7, v * 3.1) * 128 + seed * 40 + grain(x, y) * 18;
          return seed > 0.62
            ? [t * 1.08, t * 0.9, t * 0.68, 0.78, 0.46]
            : [t * 0.95, t * 0.97, t, 0.78, 0.46];
        },
      };
    }

    /* ──────────────────────────────────────────────────────────────
     * 마감 세트 표면 (2026-09-14, Chris: 「벽 마감 퀄이 너무 구리다」 · 「컨셉에 맞게」)
     * 처음엔 캔버스에 단색 + 세로줄을 그린 판을 붙였다 — 노멀도 거칠기도 없어 플라스틱이었다.
     * 다른 표면과 **같은 파이프라인**(높이→노멀, 거칠기 채널)으로 굽는다.
     * 촌스러워지는 건 언제나 채도다 — 전부 회색 쪽으로 눌러 잡는다.
     * ────────────────────────────────────────────────────────────── */
    // 회녹색 수성 페인트 — 붓결 + 위에서 흘러내린 물얼룩. 1987 년 교실 벽
    case 'wall_school': {
      const brush = fbm(2101, [[60, 1], [160, 0.5]]);
      const stain = fbm(2131, [[2, 1], [5, 0.5]]);
      const base = hexToRgb('#8d938a');
      return {
        tile: 1.6,
        normalStrength: 0.22,
        shade(x, y) {
          const b = brush(x, y * 0.35);                 // 가로로 긴 붓결
          const st = smooth(0.55, 0.9, stain(x * 0.6, y * 0.25)) * (1 - y * 0.6);   // 위가 짙다
          const k = 0.94 + b * 0.08 - st * 0.09;
          return [base[0] * k, base[1] * k, base[2] * (k - 0.01), clamp01(0.5 + b * 0.12), clamp01(0.86 + st * 0.08)];
        },
      };
    }
    // 누런 벽지 — 작은 마름모 무늬가 아주 옅게, 53cm 마다 이음선, 세월에 누렇게. 관사 살림방
    case 'wall_paper': {
      const yellow = fbm(2201, [[2, 1], [6, 0.4]]);
      const fiber = fbm(2231, [[90, 1], [220, 0.5]]);
      const base = hexToRgb('#c4b38f');
      return {
        tile: 1.06,   // 폭 53cm 롤 두 장
        normalStrength: 0.18,
        shade(x, y) {
          const u = x * 2, v = y * 2 * (1.06 / 0.36);
          const seam = 1 - smooth(0.0, 0.012, Math.min(u - Math.floor(u), 1 - (u - Math.floor(u))));
          // 마름모 점무늬 — 대비 4% 면 「무늬가 있다」로만 읽히고 그 이상이면 모텔 벽지다
          const dot = Math.max(0, 1 - Math.hypot((u * 6) % 1 - 0.5, (v * 6) % 1 - 0.5) * 3.2);
          const yl = yellow(x * 0.8, y * 0.5);
          const k = 0.9 + fiber(x, y) * 0.06 - dot * 0.04 + yl * 0.08 - seam * 0.05;
          return [base[0] * k, base[1] * (k - yl * 0.03), base[2] * (k - yl * 0.09), clamp01(0.5 + dot * 0.06 - seam * 0.2), clamp01(0.82 + fiber(x, y) * 0.06)];
        },
      };
    }
    // 흰 수성 페인트 — 롤러 스티플. 사무실·주방 위쪽 벽
    case 'wall_white': {
      const stip = fbm(2301, [[70, 1], [180, 0.6]]);
      const patch = fbm(2331, [[3, 1], [8, 0.5]]);
      const base = hexToRgb('#d9d6cc');
      return {
        tile: 1.4,
        normalStrength: 0.2,
        shade(x, y) {
          const sp = stip(x, y), pt = patch(x, y);
          const k = 0.95 + sp * 0.05 - pt * 0.04;
          return [base[0] * k, base[1] * k, base[2] * (k - 0.015), clamp01(0.5 + sp * 0.14), clamp01(0.84 + pt * 0.06)];
        },
      };
    }
    // 시멘트 블록 노출 — 390×190 블록, 줄눈 1cm. 발전기실·창고
    case 'wall_block': {
      const grain = fbm(2401, [[50, 1], [130, 0.5]]);
      const rnd = mulberry32(2411);
      const tone: number[] = []; for (let i = 0; i < 16; i++) tone.push(rnd());
      const base = hexToRgb('#8b8681');
      return {
        tile: 0.8,   // 가로 2장 × 세로 4장
        normalStrength: 1.0,
        shade(x, y) {
          const row = Math.floor(y * 4);
          const u = x * 2 + (row % 2) * 0.5, v = y * 4;
          const fu = u - Math.floor(u), fv = v - Math.floor(v);
          const edge = Math.min(Math.min(fu, 1 - fu) * 0.39, Math.min(fv, 1 - fv) * 0.19);   // 미터 단위 거리
          const grout = 1 - smooth(0.0, 0.012, edge);
          const t = tone[((Math.floor(u) & 3) + (row & 3) * 4) & 15];
          const g = grain(x, y);
          const k = 0.9 + t * 0.12 + g * 0.06 - grout * 0.28;
          return [base[0] * k, base[1] * k, base[2] * (k - 0.01), clamp01(0.75 + g * 0.1 - grout * 0.7), clamp01(0.9 - grout * 0.05)];
        },
      };
    }
    // 흰 벽 타일 150mm — 주방 허리. 매끈, 줄눈은 거칠다
    case 'wall_tile': {
      const speck = fbm(2501, [[60, 1]]);
      const rnd = mulberry32(2511);
      const N = 4; const tone: number[] = []; for (let i = 0; i < N * N; i++) tone.push(rnd());
      const base = hexToRgb('#e2e0d9');
      return {
        tile: 0.6,
        normalStrength: 0.9,
        shade(x, y) {
          const tx = x * N, ty = y * N;
          const fx = tx - Math.floor(tx), fy = ty - Math.floor(ty);
          const edge = Math.min(Math.min(fx, 1 - fx), Math.min(fy, 1 - fy));
          const grout = 1 - smooth(0.0, 0.035, edge);
          const t = tone[(Math.floor(tx) % N) + (Math.floor(ty) % N) * N];
          const k = mix(0.94, 1.03, t) - grout * 0.3 + speck(x, y) * 0.02;
          return [base[0] * k, base[1] * k, base[2] * (k - 0.01), clamp01(0.8 - grout * 0.8), clamp01(mix(0.18, 0.92, grout))];
        },
      };
    }
    // 나무 징두리 — 세로 널 90mm, V 홈. 바랜 호두색. 교실 허리
    case 'wains_wood': {
      const grain = fbm(2601, [[6, 1], [40, 0.5], [120, 0.3]]);
      const rnd = mulberry32(2611);
      const tone: number[] = []; for (let i = 0; i < 10; i++) tone.push(rnd());
      const base = hexToRgb('#66533f');
      return {
        tile: 0.9,   // 널 10장
        normalStrength: 0.8,
        shade(x, y) {
          const u = x * 10, fu = u - Math.floor(u);
          const groove = 1 - smooth(0.0, 0.07, Math.min(fu, 1 - fu));   // 널 사이 V 홈
          const t = tone[Math.floor(u) % 10];
          const g = grain(x * 0.15 + t, y);                             // 결은 세로로 흐른다
          const k = 0.88 + t * 0.14 + g * 0.1 - groove * 0.35;
          return [base[0] * k, base[1] * (k - 0.01), base[2] * (k - 0.03), clamp01(0.6 + g * 0.08 - groove * 0.6), clamp01(0.62 + g * 0.08 + groove * 0.2)];
        },
      };
    }
    // 장판 — 누렇게 바랜 비닐. 1.8m 폭 이음, 옅은 엠보. 관사 살림방 바닥
    case 'vinyl': {
      const emb = fbm(2701, [[80, 1], [200, 0.5]]);
      const wear = fbm(2731, [[2, 1], [5, 0.5]]);   // 격자 수는 정수라야 한다 — 1.5 를 줬더니 NaN 이 나와 장판이 새까맸다
      const base = hexToRgb('#c7a765');
      return {
        tile: 1.8,
        normalStrength: 0.25,
        shade(x, y) {
          const fx = x - Math.floor(x);
          const seam = 1 - smooth(0.0, 0.006, Math.min(fx, 1 - fx));
          const w = wear(x, y), e = emb(x, y);
          const k = 0.92 + e * 0.05 + w * 0.08 - seam * 0.12;
          return [base[0] * k, base[1] * (k - w * 0.02), base[2] * (k - w * 0.08), clamp01(0.5 + e * 0.1 - seam * 0.25), clamp01(0.42 + w * 0.2)];
        },
      };
    }
    // 흰 바닥 타일 300mm — 주방·다용도실
    case 'tile_white': {
      const speck = fbm(2801, [[50, 1], [120, 0.5]]);
      const wear = fbm(2831, [[4, 1], [10, 0.4]]);
      const rnd = mulberry32(2841);
      const N = 4; const tone: number[] = []; for (let i = 0; i < N * N; i++) tone.push(rnd());
      const base = hexToRgb('#d6d3cb');
      return {
        tile: 1.2,
        normalStrength: 1.0,
        shade(x, y) {
          const tx = x * N, ty = y * N;
          const fx = tx - Math.floor(tx), fy = ty - Math.floor(ty);
          const edge = Math.min(Math.min(fx, 1 - fx), Math.min(fy, 1 - fy));
          const grout = 1 - smooth(0.0, 0.03, edge);
          const t = tone[(Math.floor(tx) % N) + (Math.floor(ty) % N) * N];
          const w = wear(x, y);
          const k = mix(0.92, 1.04, t) - w * 0.08 - grout * 0.3 + speck(x, y) * 0.03;
          return [base[0] * k, base[1] * k, base[2] * (k - 0.015), clamp01(0.8 - grout * 0.8), clamp01(mix(0.26 + w * 0.2, 0.92, grout))];
        },
      };
    }
    case 'plaster': {
      const broad = fbm(101, [
        [3, 1],
        [7, 0.5],
      ]);
      const fine = fbm(151, [
        [24, 1],
        [64, 0.6],
        [160, 0.35],
      ]);
      const base = hexToRgb('#7e7669');
      return {
        tile: 2.4,
        normalStrength: 0.55,
        shade(x, y) {
          // 회벽: 넓은 미장 자국 위에 미세한 스티플
          const h = clamp01(broad(x, y) * 0.62 + fine(x, y) * 0.38);
          const stain = smooth(0.35, 0.85, broad(x * 0.7 + 0.2, y * 0.7));
          const k = 0.86 + h * 0.22 - stain * 0.14;
          return [
            base[0] * k,
            base[1] * (k - stain * 0.02),
            base[2] * (k - stain * 0.04),
            h,
            0.9 - h * 0.07 + stain * 0.05,
          ];
        },
      };
    }

    case 'oak': {
      const grainF = fbm(211, [
        [4, 1],
        [11, 0.55],
        [29, 0.3],
      ]);
      const fineF = fbm(233, [
        [96, 1],
        [220, 0.5],
      ]);
      const plankRnd = mulberry32(2027);
      const PLANKS = 9;
      const plankTone: number[] = [];
      const plankOff: number[] = [];
      for (let i = 0; i < PLANKS; i++) {
        plankTone.push(plankRnd());
        plankOff.push(plankRnd());
      }
      const base = hexToRgb('#5a3d27');
      return {
        tile: 2.0,
        normalStrength: 0.85,
        shade(x, y) {
          // 마루: 판자 그리드가 구조를 만들고, 결은 판자 안에서만 흐른다
          const px = x * PLANKS;
          const idx = Math.floor(px) % PLANKS;
          const inPlank = px - Math.floor(px);
          const off = plankOff[idx];
          // 판자 사이 홈 (구조의 주된 원인)
          const seam = Math.min(inPlank, 1 - inPlank);
          const groove = 1 - smooth(0.0, 0.03, seam);
          // 결: 판자 길이 방향으로 늘인 노이즈
          const grain = grainF(y * 0.35 + off, inPlank * 0.55 + off * 2);
          const micro = fineF(y * 1.4 + off, inPlank * 0.9);
          const h = clamp01(0.62 + grain * 0.24 + micro * 0.14 - groove * 0.9);
          const col = tintedWood(base, grain, plankTone[idx]);
          // 왁스 광택: 결 골은 덜 반사, 홈은 먼지가 앉아 거칠다
          const rough = clamp01(0.42 + grain * 0.26 + groove * 0.45 - plankTone[idx] * 0.06);
          return [col[0], col[1], col[2], h, rough];
        },
      };
    }

    case 'darkwood': {
      const grainF = fbm(307, [
        [5, 1],
        [17, 0.5],
        [41, 0.28],
      ]);
      const fineF = fbm(331, [[128, 1]]);
      const base = hexToRgb('#42301f');
      return {
        tile: 1.1,
        normalStrength: 0.6,
        shade(x, y) {
          const grain = grainF(x * 0.3, y * 1.25);
          const micro = fineF(x * 1.2, y * 2.0);
          const h = clamp01(0.55 + grain * 0.3 + micro * 0.15);
          const col = tintedWood(base, grain * 0.8, 0.55);
          return [col[0], col[1], col[2], h, clamp01(0.36 + grain * 0.3)];
        },
      };
    }

    case 'tile': {
      const speck = fbm(409, [
        [40, 1],
        [110, 0.5],
      ]);
      const wear = fbm(433, [
        [5, 1],
        [13, 0.4],
      ]);
      const rnd = mulberry32(4441);
      const N = 4;
      const tone: number[] = [];
      for (let i = 0; i < N * N; i++) tone.push(rnd());
      const base = hexToRgb('#b3ada2');
      return {
        tile: 1.6,
        normalStrength: 1.0,
        shade(x, y) {
          const tx = x * N;
          const ty = y * N;
          const ix = Math.floor(tx) % N;
          const iy = Math.floor(ty) % N;
          const fx = tx - Math.floor(tx);
          const fy = ty - Math.floor(ty);
          // 줄눈: 두 축의 경계 거리로 만든 골
          const edge = Math.min(Math.min(fx, 1 - fx), Math.min(fy, 1 - fy));
          const grout = 1 - smooth(0.0, 0.045, edge);
          const t = tone[iy * N + ix];
          const w = wear(x, y);
          const h = clamp01(0.8 + speck(x, y) * 0.1 - grout * 0.85);
          const k = mix(0.9, 1.06, t) - w * 0.07 - grout * 0.3;
          return [
            base[0] * k,
            base[1] * k,
            base[2] * (k - 0.02),
            h,
            // 타일면은 매끈, 줄눈은 거칠다 — 이 대비가 타일을 타일처럼 보이게 한다
            clamp01(mix(0.22 + w * 0.16, 0.94, grout)),
          ];
        },
      };
    }

    case 'concrete': {
      const coarse = fbm(509, [
        [6, 1],
        [15, 0.6],
        [37, 0.35],
      ]);
      const pit = fbm(541, [
        [70, 1],
        [180, 0.55],
      ]);
      const base = hexToRgb('#5f6165');
      return {
        tile: 2.6,
        normalStrength: 0.8,
        shade(x, y) {
          const c = coarse(x, y);
          const p = pit(x, y);
          // 기포 자국: 어두운 점이 곧 오목한 곳
          const hole = smooth(0.78, 0.98, p);
          const h = clamp01(0.6 + c * 0.28 - hole * 0.7);
          const k = 0.84 + c * 0.24 - hole * 0.22;
          return [base[0] * k, base[1] * k, base[2] * (k + 0.02), h, clamp01(0.8 + c * 0.14 + hole * 0.08)];
        },
      };
    }

    case 'ceiling': {
      const f = fbm(601, [
        [8, 1],
        [22, 0.5],
        [60, 0.3],
      ]);
      /**
       * **천장이 화면에서 검은 구멍으로 나왔다.**
       *
       * `#3a332c` 는 선형으로 0.04 다. 형광등은 아래를 비추므로 천장에
       * 닿는 빛은 바닥에서 되받는 것뿐인데, 거기에 GTAO 까지 곱하면 남는
       * 게 없다 — 별동 실내 컷마다 화면 위 삼분의 일이 통째로 먹칠이었다.
       * 실제 분교 천장은 흰 석고 텍스가고, 어둡게 보이는 것은 색이 아니라
       * 빛이 안 닿아서다. **색을 실물대로 올리고 어둠은 조명이 만들게 한다.**
       */
      const base = hexToRgb('#8a8074');
      return {
        tile: 2.2,
        normalStrength: 0.4,
        shade(x, y) {
          const h = clamp01(f(x, y));
          const k = 0.85 + h * 0.25;
          return [base[0] * k, base[1] * k, base[2] * k, h, 0.94 - h * 0.05];
        },
      };
    }
  }
}

// --- 텍스처 합성 ---

export interface SurfaceMaterial {
  material: THREE.MeshStandardMaterial;
  maps: Record<Exclude<ChannelView, 'off'>, THREE.Texture>;
  tile: number;
}

function texFrom(data: Uint8ClampedArray, srgb: boolean, aniso: number) {
  const cv = document.createElement('canvas');
  cv.width = cv.height = RES;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(RES, RES);
  img.data.set(data);
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(cv);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = aniso;
  // 밉맵 생성 — 원거리에서 고주파 노멀이 반짝이지 않게 하는 1차 방어선
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  return t;
}

function buildSurfaceMaterial(id: SurfaceId, aniso: number): SurfaceMaterial {
  const s = makeSurface(id);
  const n = RES * RES;
  const albedo = new Uint8ClampedArray(n * 4);
  const orm = new Uint8ClampedArray(n * 4);
  const normal = new Uint8ClampedArray(n * 4);
  const height = new Float32Array(n);

  for (let y = 0; y < RES; y++) {
    for (let x = 0; x < RES; x++) {
      const i = y * RES + x;
      const [r, g, b, h, rough] = s.shade(x / RES, y / RES);
      albedo[i * 4] = r;
      albedo[i * 4 + 1] = g;
      albedo[i * 4 + 2] = b;
      albedo[i * 4 + 3] = 255;
      height[i] = h;
      // three 규약: aoMap=R, roughnessMap=G, metalnessMap=B → 한 장으로 세 채널을 공급한다
      orm[i * 4 + 1] = rough * 255;
      orm[i * 4 + 2] = 0;
      orm[i * 4 + 3] = 255;
    }
  }

  // AO 와 노멀을 같은 height 에서 뽑는다 (채널이 같은 표면을 서술하도록)
  const at = (x: number, y: number) => height[(((y % RES) + RES) % RES) * RES + (((x % RES) + RES) % RES)];
  for (let y = 0; y < RES; y++) {
    for (let x = 0; x < RES; x++) {
      const i = y * RES + x;
      const dx = at(x + 1, y) - at(x - 1, y);
      const dy = at(x, y + 1) - at(x, y - 1);
      const nx = -dx * s.normalStrength * 4;
      const ny = -dy * s.normalStrength * 4;
      const len = Math.hypot(nx, ny, 1);
      normal[i * 4] = ((nx / len) * 0.5 + 0.5) * 255;
      normal[i * 4 + 1] = ((ny / len) * 0.5 + 0.5) * 255;
      normal[i * 4 + 2] = (1 / len) * 0.5 * 255 + 127.5;
      normal[i * 4 + 3] = 255;

      // 오목한 곳이 어둡다 — 넓은 반경으로 주변보다 낮은 정도를 본다
      const around = (at(x + 3, y) + at(x - 3, y) + at(x, y + 4) + at(x, y - 4)) / 4;
      const ao = clamp01(1 - Math.max(0, around - height[i]) * 2.4);
      orm[i * 4] = (0.35 + ao * 0.65) * 255;
    }
  }

  const maps = {
    albedo: texFrom(albedo, true, aniso),
    normal: texFrom(normal, false, aniso),
    roughness: texFrom(orm, false, aniso),
    ao: texFrom(orm, false, aniso),
  };

  const material = new THREE.MeshStandardMaterial({
    map: maps.albedo,
    normalMap: maps.normal,
    roughnessMap: maps.roughness,
    aoMap: maps.roughness,
    normalScale: new THREE.Vector2(1, 1),
    roughness: 1,
    metalness: 0,
    aoMapIntensity: 0.85,
  });
  // BoxGeometry/PlaneGeometry 에는 uv1 이 없다 — AO 도 uv0 를 읽게 한다
  material.aoMap!.channel = 0;
  material.userData.surfaceId = id;

  return { material, maps, tile: s.tile };
}

// --- 공개 API ---

let cache: Map<SurfaceId, SurfaceMaterial> | null = null;

/**
 * **쓸 때 굽는다.** 카탈로그가 40종을 넘으면서 전부 미리 구우면 로드가 몇 초 늘어난다 —
 * 세계가 실제로 쓰는 것만 첫 `get` 에서 굽고 캐시한다. `Map` 을 상속해 기존 `.get(id)!` 호출이 그대로 산다.
 */
class LazySurfaces extends Map<SurfaceId, SurfaceMaterial> {
  constructor(private aniso: number) { super(); }
  override get(id: SurfaceId): SurfaceMaterial | undefined {
    let v = super.get(id);
    if (!v) {
      // 패턴 마감(`무늬:색`)은 큰 무늬라 256 이면 충분하다. 여섯 집 마감으로 종류가 22개가 되자
      // 384 로는 텍스처가 90장·24메가픽셀 늘어 똥컴에서 렉이 났다(2026-09-15 Chris). 굽는 시간도 절반
      const keep = RES;
      if (String(id).includes(':')) RES = Math.min(RES, 256);
      try { v = buildSurfaceMaterial(id, this.aniso); } finally { RES = keep; }
      super.set(id, v);
    }
    return v;
  }
}
export function surfaces(renderer: THREE.WebGLRenderer): Map<SurfaceId, SurfaceMaterial> {
  if (cache) return cache;
  const aniso = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  const m = new LazySurfaces(aniso);
  // 세계가 늘 쓰는 것은 미리 — 첫 프레임에 한꺼번에 구워지며 멎지 않게
  for (const id of ['plaster', 'oak', 'tile', 'concrete', 'darkwood', 'ceiling'] as SurfaceId[]) m.get(id);
  cache = m;
  return cache;
}

/**
 * 지오메트리 UV 를 실제 크기 기준으로 다시 씌운다.
 * BoxGeometry 는 면마다 0..1 UV 라 긴 벽에서 텍스처가 늘어난다 — 텍셀 밀도를 통일한다.
 */
export function worldUV(geo: THREE.BufferGeometry, tile: number) {
  const pos = geo.attributes.position;
  const nor = geo.attributes.normal;
  const uv = geo.attributes.uv as THREE.BufferAttribute | undefined;
  if (!pos || !nor || !uv) return geo;
  for (let i = 0; i < pos.count; i++) {
    const ax = Math.abs(nor.getX(i));
    const ay = Math.abs(nor.getY(i));
    const az = Math.abs(nor.getZ(i));
    let u: number;
    let v: number;
    if (ax >= ay && ax >= az) {
      u = pos.getZ(i);
      v = pos.getY(i);
    } else if (ay >= ax && ay >= az) {
      u = pos.getX(i);
      v = pos.getZ(i);
    } else {
      u = pos.getX(i);
      v = pos.getY(i);
    }
    uv.setXY(i, u / tile, v / tile);
  }
  uv.needsUpdate = true;
  return geo;
}

/** 채널 디버그 뷰 — 조명 없이 해당 채널을 그대로 본다 */
export function setChannelView(view: ChannelView, scene: THREE.Scene) {
  const all = cache;
  if (!all) return;
  for (const s of all.values()) {
    if (view === 'off') {
      s.material.map = s.maps.albedo;
      s.material.normalMap = s.maps.normal;
      s.material.roughnessMap = s.maps.roughness;
    } else {
      s.material.map = s.maps[view];
      s.material.normalMap = null;
      s.material.roughnessMap = null;
    }
    s.material.needsUpdate = true;
  }
  // 채널을 그대로 읽으려면 조명을 빼야 한다
  let flat = scene.getObjectByName('__channelLight') as THREE.AmbientLight | undefined;
  if (!flat) {
    flat = new THREE.AmbientLight(0xffffff, 0);
    flat.name = '__channelLight';
    scene.add(flat);
  }
  flat.intensity = view === 'off' ? 0 : 3.2;
  scene.traverse((o) => {
    if (!(o instanceof THREE.Light) || o === flat) return;
    if (view === 'off') {
      if (o.userData.visibleBeforeDebug !== undefined) {
        o.visible = o.userData.visibleBeforeDebug as boolean;
        delete o.userData.visibleBeforeDebug;
      }
    } else if (o.userData.visibleBeforeDebug === undefined) {
      o.userData.visibleBeforeDebug = o.visible;
      o.visible = false;
    }
  });
}
